import { Badge } from "@/components/ui/badge";

/**
 * One mapping from a catalogue status to a colour, for the whole app.
 *
 * `Badge` already offers success/warning/danger/info, which is the hard half of
 * the problem: it means every screen picks its own. Nothing stops the product
 * list showing `draft` as amber while the dashboard shows it as grey, and a
 * colour that means "needs attention" on one screen and "fine" on another is
 * worse than no colour, because an operator learns it and is then wrong.
 *
 * The mapping itself is a claim about what these statuses MEAN:
 *
 * - `active` is success. It is on sale; nothing is owed.
 * - `draft` is warning, not neutral. A draft is unfinished work that somebody
 *   has to come back to, and this screen exists because the public browse path
 *   cannot see them -- grey would file them under "fine".
 * - `inactive` is NEUTRAL, not danger. This was the one worth checking rather
 *   than deciding: `products-dashboard` already had a documented mapping with
 *   `inactive: "default"`, and colouring it red here would have introduced the
 *   very drift this component exists to prevent -- while claiming to prevent it.
 *   The existing reading is also the better one: an inactive product is a
 *   resting state somebody chose, not a fault.
 * - Verification is separate on purpose: `human_reviewed` is info rather than
 *   success, because somebody checking a product is not the same claim as the
 *   product being sellable, and colouring both green conflates them.
 */
const STATUS_VARIANTS: Record<
	string,
	"default" | "success" | "warning" | "danger" | "info"
> = {
	active: "success",
	draft: "warning",
	inactive: "default",
	human_reviewed: "info",
	auto_verified: "default",
	unverified: "default",

	/*
	 * Import run states (plan 011). Eight of them, and without entries here all
	 * eight render in the same neutral tone -- a runs list where `failed` and
	 * `committed` look alike is worse than one with no colour at all, because an
	 * operator scanning it will believe what they see.
	 *
	 * - `failed` is danger. Something went wrong and nothing was applied.
	 * - `committed` is success. It landed.
	 * - `staged` and `resolved` are WARNING for the same reason `draft` is:
	 *   unfinished work somebody has to come back to. A staged run that nobody
	 *   commits is a price list that never reached the catalogue.
	 * - `resolving` and `committing` are info: work in flight, nothing owed.
	 * - `reverted` is NEUTRAL, not danger, on the same reading as `inactive`: a
	 *   revert is a decision somebody made, not a fault.
	 */
	failed: "danger",
	committed: "success",
	staged: "warning",
	resolved: "warning",
	resolving: "info",
	committing: "info",
	reverted: "default",
	// Neutral, like `reverted`: a decision somebody made to drop the run.
	discarded: "default",

	/*
	 * Import ROW states, a separate vocabulary from the run's.
	 *
	 * Without these the run screen rendered `error` and `exact` identically --
	 * a row that could not be read looking exactly like one confidently matched,
	 * on the screen whose whole job is telling them apart.
	 *
	 * - `error` is danger: the row cannot be committed at all.
	 * - `ambiguous` is warning: it needs a person, which is work owed.
	 * - `exact` is success and `new` is info: both will be applied, but they do
	 *   different things, and a reviewer scanning for surprises wants to see
	 *   which rows are touching an existing product.
	 * - `pending`, `unmatched` and `strong` are neutral -- undecided, or a tier
	 *   that is not built.
	 */
	error: "danger",
	ambiguous: "warning",
	exact: "success",
	new: "info",
	pending: "default",
	unmatched: "default",
	strong: "default",
};

/** `human_reviewed` is a column value, not something to show an operator. */
function label(status: string): string {
	return status.replace(/_/g, " ");
}

export function StatusBadge({
	status,
	className,
}: {
	status: string;
	className?: string;
}) {
	/*
	 * No `?? "default"` here. `Badge` declares `defaultVariants: { variant:
	 * "default" }` through cva, so an unmapped status already renders in the
	 * neutral tone -- the fallback was a line no change to this file could make
	 * fail, under a test named for catching exactly that.
	 */
	return (
		<Badge variant={STATUS_VARIANTS[status]} className={className}>
			{label(status)}
		</Badge>
	);
}
